import { Section } from "@redotech/redo-model/email-builder";
import { Provider } from "@redotech/redo-model/order";
import { ProductFilter } from "@redotech/redo-model/product-filters/product-filters-db-parser";
import {
  ProductRecommendationType,
  UniqueProductFilterType,
  comparisonOperatorEvaluator,
} from "@redotech/redo-model/product-filters/product-filters-types";
import {
  RedoRecommendedProduct,
  redoRecommendedProductSchema,
} from "@redotech/redo-model/redo-recommended-product-schema";
import {
  ProductRecommendationIntent,
  ProductSortKeys,
} from "@redotech/shopify-client/storefront.graphql";
import { filterTruthy } from "@redotech/util/array";
import {
  Gid,
  ShopifyIdType,
  convertShopifyIdToGid,
} from "@redotech/util/shopify-id";
import { assertNever } from "@redotech/util/type";
import {
  PRODUCT_INFO_FRAGMENT,
  ProductInfo,
  getCollection,
  getCollections,
  getProduct,
  getProductWithMetafield,
  getProducts,
  getProductsByGids,
} from "./products";
import {
  GetProductRecommendationsQuery,
  GetProductRecommendationsQueryVariables,
} from "./shopify-storefront.graphql";
import { ShopifyStorefrontClient, handleErrors } from "./storefront-client";

const getProductRecommendationsQuery = /* GraphQL */ `
  ${PRODUCT_INFO_FRAGMENT}
  query getProductRecommendations(
    $productId: ID!
    $intent: ProductRecommendationIntent!
  ) {
    productRecommendations(productId: $productId, intent: $intent) {
      ...ProductInfo
    }
  }
`;

export const getProductRecommendations = async (
  client: ShopifyStorefrontClient,
  productId: Gid,
): Promise<ProductInfo[]> => {
  const { data, errors } = await client.request<
    GetProductRecommendationsQuery,
    GetProductRecommendationsQueryVariables
  >(getProductRecommendationsQuery, {
    productId: productId,
    intent: ProductRecommendationIntent.Related,
  });

  handleErrors(errors);

  return data?.productRecommendations || [];
};

const validProduct = (product: ProductInfo) =>
  product.vendor !== "re:do" && product.variants.nodes?.[0]?.requiresShipping;

export async function getRecommendationsForMultipleProducts({
  storefrontClient,
  productIds,
  numRecommendations,
  productIdsToExclude = [],
  logException = console.error,
}: {
  storefrontClient: ShopifyStorefrontClient;
  productIds: Gid[];
  numRecommendations: number;
  productIdsToExclude?: Gid[];
  logException?: (msg: string, error: unknown, context: any) => void;
}): Promise<ProductInfo[]> {
  const originalProducts = new Set(productIds);
  const recommendations = (
    await Promise.all(
      productIds.map(async (id: any) => {
        try {
          return await getProductRecommendations(storefrontClient, id);
        } catch (e) {
          logException("Error getting product recommendations", e, {
            productId: id,
            productIds,
            numRecommendations,
          });
          return [];
        }
      }),
    )
  )
    .flat()
    .filter((p) => p.availableForSale);

  const counts = recommendations
    .filter(validProduct)
    .reduce(
      (
        acc: Record<string, { count: number; product: ProductInfo }>,
        product,
      ) => {
        if (!acc[product.id]) {
          acc[product.id] = { count: 0, product };
        }
        acc[product.id].count++;
        return acc;
      },
      {},
    );

  const topRecommendations = Object.entries(counts)
    .filter(
      ([id]) =>
        !productIdsToExclude.includes(
          convertShopifyIdToGid(id, ShopifyIdType.Product),
        ),
    )
    .sort((a, b) => b[1].count - a[1].count)
    .slice(0, numRecommendations)
    .map(([_, { product }]) => product);

  try {
    // If we didn't find enough recommendations,
    // fill the rest of the list with the best sellers.
    if (topRecommendations.length < numRecommendations) {
      const iterator = await getBestSellingProducts(
        storefrontClient,
        numRecommendations - topRecommendations.length,
      );
      for await (const product of iterator) {
        if (topRecommendations.length >= numRecommendations) {
          break;
        }
        // Don't push products we're already recommending,
        // or products we are trying to make recommendations for.
        if (
          validProduct(product) &&
          !counts[product.id] &&
          !originalProducts.has(
            convertShopifyIdToGid(product.id, ShopifyIdType.Product),
          ) &&
          product.availableForSale
        ) {
          counts[product.id] = { count: 1, product };
          topRecommendations.push(product);
        }
      }
    }
  } catch (e) {
    logException("Error getting top products", e, {
      productIds,
      numRecommendations,
    });
  }
  return topRecommendations;
}

const getBestSellingProducts = async (
  client: ShopifyStorefrontClient,
  amount: number = 10,
): Promise<ProductInfo[]> => {
  const products: ProductInfo[] = [];
  const iterator = getProducts(client, {
    first: amount,
    query: `-vendor:re\\:do`,
    sortKey: ProductSortKeys.BestSelling,
  });
  for await (const product of iterator) {
    products.push(product);
    if (products.length >= amount) {
      break;
    }
  }
  return products;
};

export const getRecommendedBestSellingProducts = async (
  client: ShopifyStorefrontClient,
  amount: number = 10,
): Promise<RedoRecommendedProduct[]> => {
  const products = await getBestSellingProducts(client, amount);
  return products
    .map((product) => toRedoRecommendedProduct(product))
    .filter((p) => !!p);
};

export const getRecommendedProductsByTags = async (
  client: ShopifyStorefrontClient,
  tags: string[],
  amount: number = 250,
  sortKey: ProductSortKeys = ProductSortKeys.BestSelling,
): Promise<RedoRecommendedProduct[]> => {
  const products: ProductInfo[] = [];
  try {
    const iterator = getProducts(client, {
      first: amount,
      query: `tag:${tags.map((tag: string) => `"${tag}"`).join(" OR ")}`,
      sortKey: sortKey,
    });
    for await (const product of iterator) {
      products.push(product);
      if (products.length >= amount) {
        break; // Stop iterating once we reach the desired amount
      }
    }
  } catch (e) {
    console.error("Error getting top products", e);
  }
  const productsWithImageAndPrice = products
    .map((product) => toRedoRecommendedProduct(product))
    .filter((p) => !!p);

  return productsWithImageAndPrice;
};

export const getRecommendedProductsByCollection = async (
  client: ShopifyStorefrontClient,
  collectionId: Gid,
  numRecommendations: number,
): Promise<RedoRecommendedProduct[]> => {
  const collection = await getCollection(client, collectionId);
  if (!collection) {
    throw new Error("Collection not found");
  }

  const items = collection.products.nodes
    .map((product: ProductInfo) => toRedoRecommendedProduct(product))
    .filter((p) => !!p)
    .slice(0, numRecommendations);
  return items;
};

export const getProductsByCollections = async (
  client: ShopifyStorefrontClient,
  collectionIds: Gid[],
  numRecommendations: number,
): Promise<RedoRecommendedProduct[]> => {
  const collections = await getCollections(client, collectionIds);
  const products = collections
    .flatMap((collection) => collection?.products.nodes ?? [])
    .filter((product) => product.availableForSale);
  const items = products
    .map((product: ProductInfo) => toRedoRecommendedProduct(product))
    .filter((p) => !!p)
    .slice(0, numRecommendations);
  return items;
};

export const getProductsById = async (
  client: ShopifyStorefrontClient,
  // When a product id is passed in as a part of a query, it is expected to be the numeric id, not the gid.
  productIds: Gid[],
): Promise<ProductInfo[]> => {
  try {
    const products = await getProductsByGids(client, productIds);
    return products;
  } catch (e) {
    console.error("Error getting products by id", e);
    return [];
  }
};

export const getProductsByShopifyRecommendations = async ({
  storefrontClient,
  productIds,
  numRecommendations = 15,
  filterCurrentLineItems = false,
  logException,
}: {
  storefrontClient: any;
  productIds: Gid[];
  numRecommendations?: number;
  filterCurrentLineItems?: boolean;
  logException?: (msg: string, error: unknown, context: any) => void;
}): Promise<RedoRecommendedProduct[]> => {
  // get extra recommended products so that if any have low inventory, we can filter them out
  const recommendations = (
    await getRecommendationsForMultipleProducts({
      storefrontClient,
      productIds,
      numRecommendations,
      productIdsToExclude: filterCurrentLineItems ? productIds : [],
      logException,
    })
  )
    .map((p) => toRedoRecommendedProduct(p))
    .filter((p) => !!p);
  return recommendations;
};

export const getProductsByProductMetafieldInOrder = async (
  storefrontClient: ShopifyStorefrontClient,
  productIds: Gid[],
  metafield: { key: string; namespace: string; metaobjectKey?: string },
  numRecommendations: number,
  filterCurrentLineItems: boolean = false,
): Promise<RedoRecommendedProduct[]> => {
  if (!metafield?.key || !metafield?.namespace) {
    console.error("Metafield key or namespace not found in settings");
    return getProductsByShopifyRecommendations({
      storefrontClient,
      productIds,
      numRecommendations,
      filterCurrentLineItems,
    });
  }

  const productsWithMetafields = await successfulResults(productIds, (id) =>
    getProductWithMetafield(
      storefrontClient,
      id,
      metafield.key,
      metafield.namespace,
      metafield.metaobjectKey,
    ),
  );

  const recommendations = await successfulResults(
    productsWithMetafields,
    (product) => getProduct(storefrontClient, product.id),
  );

  const items = recommendations
    .map((product) => toRedoRecommendedProduct(product))
    .filter((p) => !!p)
    .filter((p) =>
      filterCurrentLineItems
        ? !productIds.includes(
            convertShopifyIdToGid(p.id, ShopifyIdType.Product),
          )
        : true,
    );

  if (items.length < numRecommendations) {
    // Couldn't pull enough products from metafields, filling in remaining with recommendations
    const recommendedProducts = await getProductsByShopifyRecommendations({
      storefrontClient,
      productIds,
      numRecommendations: numRecommendations - items.length,
      filterCurrentLineItems,
    });
    const uniqueRecommendedProducts = recommendedProducts.filter(
      (p) => !items.some((item) => item.id === p.id),
    );
    return [...items, ...uniqueRecommendedProducts];
  }
  return items;
};

function isSuccessful<T>(
  result: PromiseSettledResult<T>,
): result is PromiseFulfilledResult<T> {
  return result.status === "fulfilled" && !!result.value;
}

async function successfulResults<A, B>(
  inputValues: A[],
  fn: (value: A) => Promise<B>,
): Promise<NonNullable<B>[]> {
  return (await Promise.allSettled(inputValues.map(fn)))
    .filter(isSuccessful)
    .map((result) => result.value as NonNullable<B>);
}

export async function getManualProducts({
  storeUrl,
  storefrontAccessToken,
  sectionProps,
}: {
  storeUrl: string;
  storefrontAccessToken: string | null | undefined;
  sectionProps: Section.Products;
}): Promise<RedoRecommendedProduct[]> {
  const productIds =
    sectionProps.manuallySelectedProducts?.map((p) => p?.productId) ?? [];

  if (!productIds.length || !storefrontAccessToken) {
    return [];
  }
  const client = new ShopifyStorefrontClient(storeUrl, storefrontAccessToken!);

  const productsById = await getProductsById(
    client,
    filterTruthy(productIds).map((id) =>
      convertShopifyIdToGid(id, ShopifyIdType.Product),
    ),
  );
  const result: RedoRecommendedProduct[] = productsById
    .map((p) => toRedoRecommendedProduct(p))
    .filter((p) => !!p);
  return result.filter((product) => product.vendor !== "re:do");
}

export const getProductsByProductRecommendationFilter = async (
  client: ShopifyStorefrontClient,
  productRecommendationFilter: ProductFilter,
  productIds: Gid[],
): Promise<RedoRecommendedProduct[]> => {
  let products: RedoRecommendedProduct[] = [];
  switch (productRecommendationFilter.productRecommendationType) {
    case ProductRecommendationType.TAG:
      products = await getRecommendedProductsByTags(
        client,
        productRecommendationFilter.tags,
        10,
      );
      break;
    case ProductRecommendationType.COLLECTION:
      products = await getRecommendedProductsByCollection(
        client,
        convertShopifyIdToGid(
          productRecommendationFilter.collectionId,
          ShopifyIdType.Collection,
        ),
        10,
      );
      break;
    case ProductRecommendationType.METAFIELD: {
      const metafield = productRecommendationFilter.metafield;
      if (!metafield) {
        throw new Error("Metafield not found");
      }
      products = await getProductsByProductMetafieldInOrder(
        client,
        productIds,
        metafield,
        10,
      );
      break;
    }
    case ProductRecommendationType.PROVIDER_RECOMMENDATIONS:
      products = await getProductsByShopifyRecommendations({
        storefrontClient: client,
        productIds,
        numRecommendations: 10,
        filterCurrentLineItems: true,
      });
      break;
    case ProductRecommendationType.BEST_SELLERS:
      products = await getRecommendedBestSellingProducts(client, 10);
      break;
    default:
      throw new Error("Unsupported product recommendation filter type");
  }

  //TODO: these should really be added as filters to the queries above
  const filteredProducts = products.filter((product: RedoRecommendedProduct) =>
    // If there are no additional filters, keep all products
    !productRecommendationFilter.additionalProductFilters?.length
      ? true
      : productRecommendationFilter.additionalProductFilters?.some((filter) => {
          switch (filter.type) {
            case UniqueProductFilterType.TAG:
              return filter.tags.some((tag) => product.tags.includes(tag));
            case UniqueProductFilterType.COLLECTION:
              return filter.collectionIds.some((collectionId) =>
                product.collections.nodes.some(
                  (collection) => collection.id === collectionId,
                ),
              );
            case UniqueProductFilterType.METAFIELD:
              return filter.metafields.some(
                (metafield) =>
                  //TODO we need to add metafield support here, currently, the storefront api does not support metafields in the search query
                  true,
              );
            case UniqueProductFilterType.INVENTORY:
              return comparisonOperatorEvaluator[filter.comparisonOperator](
                product.variants.nodes.reduce(
                  (acc, variant) => acc + (variant.quantityAvailable ?? 0),
                  0,
                ),
                filter.inventory,
              );
            case UniqueProductFilterType.PRICE:
              return comparisonOperatorEvaluator[filter.comparisonOperator](
                product.price,
                filter.price,
              );
            default:
              assertNever(filter);
          }
        }),
  );

  return filteredProducts;
};

const toRedoRecommendedProduct = (
  shopifyProduct: ProductInfo,
): RedoRecommendedProduct | null => {
  const product = {
    ...shopifyProduct,
    variants: {
      nodes: shopifyProduct.variants.nodes.map((variant) => {
        return {
          ...variant,
          price: {
            amount: parseFloat(variant.price.amount),
            currencyCode: variant.price.currencyCode,
          },
        };
      }),
    },
    altImage: {
      url: shopifyProduct?.images?.nodes?.[1]?.url,
      altText: shopifyProduct?.images?.nodes?.[1]?.altText ?? "",
    },
    price: Number(shopifyProduct.priceRange.minVariantPrice.amount),
    provider: Provider.SHOPIFY,
  };

  const parsedProduct = redoRecommendedProductSchema.safeParse(product);
  if (parsedProduct.success) {
    return parsedProduct.data;
  }
  console.error("Error parsing product", parsedProduct.error);
  return null;
};
